import { Configuration } from '../types';

export interface SampleSet {
  id: string;
  name: string;
  description: string;
  /** Plain-English purpose for each category, in the same order as config.categories */
  categoryDescriptions: string[];
  config: Configuration;
}

export const SAMPLE_SETS: SampleSet[] = [
  {
    id: 'mixed-practice',
    name: 'Mixed Practice',
    description:
      'A balanced teaching set mixing random boards with weak opens, Stayman, and strong openings against a fit.',
    categoryDescriptions: [
      'Completely random deals with no special requirements — useful fillers and warm-up boards.',
      'Weak 2/3/4 openings where partner has enough strength and a fit to support.',
      '1NT openings where responder has enough points and a 4-card major for Stayman.',
      'Strong 2♣ / 2NT openings against opponents who hold a long major fit with shape.',
    ],
    config: {
      totalDeals: 28,
      biddingSystem: 'NZ_Acol',
      categories: [
        {
          name: 'Random Hands',
          weight: 10,
          setDealer: 'None',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['DontCare'],
            },
          },
        },
        {
          name: 'Weak 2,3 or 4 - With partner support',
          weight: 10,
          setDealer: 'Exact',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: [
                '2D', '2H', '2S', '3C', '3D', '3H', '3S', '4C', '4D', '4H', '4S',
              ],
            },
            responder: {
              hcpMin: 12,
            },
            openingPartnership: {
              fit: 'Any',
            },
          },
        },
        {
          name: '1NT Open - Possible Stayman',
          weight: 10,
          setDealer: 'Close',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['1NT'],
            },
            responder: {
              hcpMin: 11,
              fourCardMajor: 'Required',
            },
            openingPartnership: {
              totalHcpMax: 29,
            },
          },
        },
        {
          name: 'Strong open - Opponent fit',
          weight: 10,
          setDealer: 'Exact',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['2C', '2NT'],
            },
            opponentPartnership: {
              lhoOvercall: [],
              minAcesCount: 1,
              totalVoids: 2,
              totalSingletons: 1,
              fit: 'Major',
              fitLength: 10,
            },
          },
        },
      ],
    },
  },
  {
    id: 'beginner-practice',
    name: 'Beginner Practice',
    description:
      'Simple starting templates for newer players — random boards, balanced hands, and straightforward 1NT auctions.',
    categoryDescriptions: [
      'Random deals so beginners see a natural mix of hands.',
      'Balanced opening hands in a typical 1NT point range.',
      '1NT openings with a responder who has enough points to invite or bid game.',
    ],
    config: {
      totalDeals: 28,
      biddingSystem: 'NZ_Acol',
      categories: [
        {
          name: 'Random Hands',
          weight: 12,
          setDealer: 'None',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['DontCare'],
            },
          },
        },
        {
          name: 'Balanced 1NT range',
          weight: 10,
          setDealer: 'Exact',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['1NT'],
              balanced: 'BalancedNoFiveCardMajor',
              hcpMin: 12,
              hcpMax: 14,
            },
          },
        },
        {
          name: '1NT - Responder has values',
          weight: 8,
          setDealer: 'Close',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['1NT'],
            },
            responder: {
              hcpMin: 10,
            },
          },
        },
      ],
    },
  },
  {
    id: 'nt-focus',
    name: 'No Trump Focus',
    description:
      'Concentrates on NT auctions — weak NT, Stayman, and stronger NT openings for convention practice.',
    categoryDescriptions: [
      'Standard 1NT openings for auction practice.',
      '1NT with responder holding a 4-card major (Stayman situations).',
      'Stronger NT openings (2NT) for game and slam exploration.',
    ],
    config: {
      totalDeals: 24,
      biddingSystem: 'NZ_Acol',
      categories: [
        {
          name: '1NT Openings',
          weight: 12,
          setDealer: 'Exact',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['1NT'],
            },
          },
        },
        {
          name: '1NT - Possible Stayman',
          weight: 12,
          setDealer: 'Close',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['1NT'],
            },
            responder: {
              hcpMin: 11,
              fourCardMajor: 'Required',
            },
            openingPartnership: {
              totalHcpMax: 29,
            },
          },
        },
        {
          name: '2NT Openings',
          weight: 8,
          setDealer: 'Exact',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['2NT'],
            },
          },
        },
      ],
    },
  },
  {
    id: 'competitive-practice',
    name: 'Competitive Practice',
    description:
      'Deals aimed at competitive bidding — weak jumps, partner support, and contested auctions.',
    categoryDescriptions: [
      'Weak two or three-level openings where partner can raise with a fit.',
      'Suit openings where the opponents also have a meaningful fit.',
      'Random boards mixed in for variety across the set.',
    ],
    config: {
      totalDeals: 28,
      biddingSystem: 'NZ_Acol',
      categories: [
        {
          name: 'Weak jump - Partner support',
          weight: 12,
          setDealer: 'Exact',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['2D', '2H', '2S', '3C', '3D', '3H', '3S'],
            },
            responder: {
              hcpMin: 10,
            },
            openingPartnership: {
              fit: 'Any',
            },
          },
        },
        {
          name: 'Opening suit - Opponent fit',
          weight: 10,
          setDealer: 'Exact',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['1C', '1D', '1H', '1S'],
            },
            opponentPartnership: {
              fit: 'Any',
              fitLength: 8,
            },
          },
        },
        {
          name: 'Random Hands',
          weight: 8,
          setDealer: 'None',
          criteria: {
            opener: {
              allowRuleOf20: true,
              openingBids: ['DontCare'],
            },
          },
        },
      ],
    },
  },
];
